import React from 'react';
import { motion } from 'framer-motion';
import { 
  Code2, 
  Smartphone, 
  Database, 
  FileText, 
  Settings, 
  Zap,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const Services = () => {
  const easePremium = [0.22, 1, 0.36, 1];

  const services = [
    {
      icon: Code2,
      title: 'Desarrollo de Software',
      description: 'Sistemas empresariales a medida, escalables y de alto rendimiento para optimizar tus operaciones.',
      features: ['Aplicaciones web', 'Sistemas ERP/CRM', 'Plataformas SaaS']
    },
    {
      icon: FileText,
      title: 'Facturación Electrónica',
      description: 'Soluciones contables inteligentes integradas con SUNAT para gestión financiera automatizada.',
      features: ['Emisión de comprobantes', 'Reportes fiscales', 'Conciliación bancaria']
    },
    {
      icon: Smartphone,
      title: 'Aplicaciones Móviles',
      description: 'Apps nativas e híbridas para iOS y Android con experiencia de usuario premium.',
      features: ['iOS y Android', 'Diseño UX/UI', 'Publicación en stores']
    },
    {
      icon: Database,
      title: 'APIs e Integraciones',
      description: 'Arquitecturas backend robustas y APIs seguras para conectar todo tu ecosistema digital.',
      features: ['RESTful APIs', 'Integraciones', 'Seguridad enterprise']
    },
    {
      icon: Zap,
      title: 'Automatización de Procesos',
      description: 'Flujos de trabajo inteligentes que eliminan tareas manuales y reducen errores operativos.',
      features: ['Workflows automáticos', 'Reportes programados', 'Alertas inteligentes']
    },
    {
      icon: Settings,
      title: 'Consultoría Tecnológica',
      description: 'Asesoría estratégica para digitalizar tu empresa y elegir las herramientas correctas.',
      features: ['Auditoría IT', 'Roadmap digital', 'Capacitación']
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1, ease: easePremium }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: easePremium } }
  };

  return (
    <section id="servicios" className="py-16 md:py-24 lg:py-32 bg-white relative overflow-hidden">
      
      {/* Textura de fondo ultra-sutil */}
      <div 
        className="absolute inset-0 z-0 opacity-[0.02]"
        style={{
          backgroundImage: 'radial-gradient(#059669 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }}
      />

      <div className="container mx-auto px-4 md:px-6 lg:px-12 relative z-10">
        
        {/* ========================================== */}
        {/* ENCABEZADO CENTRADO Y RESPONSIVO           */}
        {/* ========================================== */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: easePremium }}
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16 lg:mb-20"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-4 uppercase tracking-wider">
            <Settings className="w-4 h-4" />
            Nuestros Servicios
          </span>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.15] mb-4 md:mb-6">
            Soluciones tecnológicas <br className="hidden md:block" />
            <span className="text-emerald-600">para cada necesidad</span>
          </h2>
          
          <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto px-2">
            Ofrecemos un ecosistema completo de servicios digitales diseñados para impulsar la eficiencia, 
            reducir costos y preparar tu empresa para el futuro.
          </p>
        </motion.div>

        {/* ========================================== */}
        {/* GRID DE SERVICIOS RESPONSIVO               */}
        {/* ========================================== */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8 mb-16 md:mb-24"
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                variants={itemVariants}
                whileHover={{ y: -4 }}
                transition={{ duration: 0.3 }}
                className="group relative bg-white rounded-2xl border border-slate-200 p-6 md:p-8 hover:border-emerald-500/30 hover:shadow-lg hover:shadow-emerald-900/5 transition-all duration-300 flex flex-col h-full"
              >
                {/* Icono */}
                <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center mb-5 md:mb-6 group-hover:bg-emerald-50 group-hover:border-emerald-100 transition-colors duration-300">
                  <Icon className="w-6 h-6 text-slate-700 group-hover:text-emerald-600 transition-colors duration-300" />
                </div>

                {/* Título y descripción */}
                <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2 md:mb-3 tracking-tight">
                  {service.title}
                </h3>
                <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-5 md:mb-6 flex-grow">
                  {service.description}
                </p>

                {/* Lista de características */}
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs md:text-sm text-slate-700">
                      <CheckCircle2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-emerald-600 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Línea de acento inferior */}
                <div className="absolute bottom-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-emerald-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            );
          })}
        </motion.div>

        {/* ========================================== */}
        {/* CTA: PROCESO DE TRABAJO (RESPONSIVO)       */}
        {/* ========================================== */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: easePremium }}
          className="bg-slate-50 rounded-2xl border border-slate-200 p-6 md:p-10 lg:p-12 mx-0 md:mx-4 lg:mx-0"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            
            {/* Texto y CTA */}
            <div className="text-center lg:text-left">
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-3 md:mb-4">
                ¿Cómo trabajamos?
              </h3>
              <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-6 md:mb-8 max-w-lg mx-auto lg:mx-0">
                Nuestro proceso está diseñado para entregar resultados tangibles en cada etapa, 
                manteniendo comunicación constante y adaptándonos a tus necesidades.
              </p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group inline-flex items-center justify-center gap-2 bg-emerald-600 text-white px-6 md:px-8 py-3 md:py-3.5 rounded-lg font-semibold text-sm md:text-base transition-all hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-600/20 w-full sm:w-auto"
              >
                Iniciar un proyecto
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </motion.button>
            </div>

            {/* Pasos del proceso */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              {[
                { step: '01', title: 'Descubrimiento', desc: 'Analizamos tus necesidades y objetivos.' },
                { step: '02', title: 'Planificación', desc: 'Creamos la arquitectura y el roadmap.' },
                { step: '03', title: 'Desarrollo Ágil', desc: 'Construimos con iteraciones rápidas.' },
                { step: '04', title: 'Soporte', desc: 'Desplegamos y brindamos soporte continuo.' }
              ].map((item, idx) => (
                <div key={idx} className="flex gap-3 md:gap-4 bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center">
                    <span className="text-xs md:text-sm font-bold text-emerald-600">{item.step}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm md:text-base mb-1">{item.title}</h4>
                    <p className="text-xs md:text-sm text-slate-600 leading-snug">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default Services;